import { ChangeEvent, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useRouter } from 'next/router';
// Context
import { useFiltersContext } from 'components/common3/PageWithFiltersWrapper/PageWithFiltersContext';
// Helpers
import { useDebouncedSearchRefetch } from 'helpers/filters';
// Api
import { GET_STORES_NAMES_LIST } from 'api/store/queries';
// Types
import {
  UserRole,
  StoresOrderBy,
  SortDirection,
} from 'api/graphql-global-types';
import {
  GetStoresNamesList,
  GetStoresNamesListVariables,
  GetStoresNamesList_stores_entities,
} from 'api/store/types/GetStoresNamesList';
// Ui
import InfiniteScrollLoader from 'ui3/InfiniteScrollLoader/InfiniteScrollLoader';
// Components
import CheckboxFilter from '../CheckboxFilter/CheckboxFilter';
// Styles
import styles from './ContentCreatorsFilter.module.scss';

const PER_PAGE = 8;

const ContentCreatorsFilter = () => {
  const router = useRouter();
  const [searchTerm, setSearchTerm] = useState(
    typeof router.query.creatorSearch === 'string'
      ? router.query.creatorSearch
      : ''
  );

  const {
    filters,
    storesListFilter,
    setFilters,
    searchKey,
    search,
  } = useFiltersContext();

  const inputVariables = useMemo(
    () => ({
      orderBy: StoresOrderBy.storeName,
      direction: SortDirection.ASC,
      limit: PER_PAGE,
      offset: 0,
      ...(storesListFilter && {
        [storesListFilter]: true,
      }),
    }),
    [storesListFilter]
  );

  const { data, fetchMore, refetch } = useQuery<
    GetStoresNamesList,
    GetStoresNamesListVariables
  >(GET_STORES_NAMES_LIST, {
    variables: {
      storeRoles: [UserRole.ContentCreator],
      input: {
        ...inputVariables,
      },
    },
    fetchPolicy: 'cache-first',
  });

  useDebouncedSearchRefetch({
    searchTerm,
    refetch,
    inputVariables,
  });

  const onCheckBoxChange = ({
    target: { id },
  }: ChangeEvent<HTMLInputElement>) => {
    const contentCreatorIds: string[] = filters?.contentCreatorIds?.includes(id)
      ? filters?.contentCreatorIds?.filter((creatorId) => creatorId !== id) ||
        []
      : [...(filters?.contentCreatorIds || []), id];

    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          creatorSearch: searchTerm || undefined,
          contentCreatorIds: contentCreatorIds.join(','),
        },
      },
      undefined,
      { shallow: false }
    );

    setFilters({ ...filters, contentCreatorIds });
  };

  const fetchMoreData = () => {
    const prevOffset = data?.stores?.offset || 0;
    const offset = prevOffset ? prevOffset + PER_PAGE : PER_PAGE;

    fetchMore({
      variables: {
        input: {
          ...inputVariables,
          offset,
          searchTerm: searchTerm.trim() || undefined,
        },
      },
    });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchTerm(value);
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          creatorSearch: value || undefined,
        },
      },
      undefined,
      { shallow: false }
    );
  };

  const creators = data?.stores?.entities || [];
  const total = data?.stores?.total;
  const hasMore = data ? creators.length !== total : false;
  const infiniteScrollHeight = !creators?.length
    ? 48
    : creators.length >= 6
    ? 240
    : creators.length * 48;

  const getIfChecked = (
    creator: GetStoresNamesList_stores_entities
  ): boolean => {
    let isChecked = Boolean(filters?.contentCreatorIds?.includes(creator?.id));

    if (
      searchKey === 'storeName' &&
      creator?.storeDetails?.storeName === search?.['storeName']
    ) {
      isChecked = true;
    }

    return isChecked;
  };

  const isFilterOpen = Boolean(
    filters?.contentCreatorIds?.length || searchTerm.trim()
  );

  return (
    <CheckboxFilter<GetStoresNamesList_stores_entities>
      title="Content Creators"
      options={creators}
      onChange={onCheckBoxChange}
      getOptionValue={(creator) => creator.id}
      getOptionLabel={(creator) => creator.storeDetails?.storeName}
      getIfChecked={(creator) => getIfChecked(creator)}
      selectedValues={filters?.contentCreatorIds || []}
      isOpen={isFilterOpen}
      searchInputValue={searchTerm}
      onSearchInputChange={handleSearchInputChange}
      infiniteScroll={{
        className: styles.contentCreatorsFilterWrapper,
        dataLength: creators.length,
        next: fetchMoreData,
        hasMore,
        height: infiniteScrollHeight,
        loader: <InfiniteScrollLoader size="small" />,
      }}
    />
  );
};

export default ContentCreatorsFilter;
